import {
  AI_CHAT_ACTION_TYPE,
  AiChatMessageRole,
  ToolConfirmationStatus,
  UserQuestionStatus,
} from 'constants/aiChat';

import { RESET_STATE } from 'store/global/actions';

import {
  applyAssistantActivity,
  applyCitationsFlagToMessages,
  applyMessageFailedToMessages,
  applyMessageFeedbackOutcome,
  applyResponseCompleteToMessages,
  applyResponseTokenToMessages,
  applySessionStoppedToMessages,
  applyToolCompletedToMessages,
  applyToolFailedToMessages,
  applyToolStartedToMessages,
  ensureStreamingAssistantPlaceholder,
  markOptimisticMessageFailed,
  reconcileSentMessage,
  removeFailedAssistantPlaceholderForMessage,
} from './aiChat.reducer.utils';
import { applyCitationsForMessage } from './aiChatCitations.reducer.utils';
import {
  applyBlockAdded,
  applyBlocksLoaded,
  applyBlockUpdated,
} from './aiChatMessageContentBlocks.reducer.utils';
import {
  applyClearPlanRevisionFlags,
  applyPlanCancelled,
  applyPlanCompleted,
  applyPlanCreated,
  applyPlanPlanningStarted,
  applyPlanRevised,
  applyPlanStepAwaitingConfirmation,
  applyPlanStepCancelled,
  applyPlanStepCompleted,
  applyPlanStepFailed,
  applyPlanStepSkipped,
  applyPlanStepStarted,
  applyPlanStepToolDispatched,
  applyPlanStepToolReturned,
  applyPlanUpdated,
} from './aiChatPlan.reducer.utils';
import {
  applySetActionPlanExpansion,
  initialActionPlanExpansionsState,
} from './aiChatPlanUi.reducer.utils';
import {
  appendPendingConfirmation,
  mergePendingConfirmationsForMessage,
  updateConfirmationStatus,
} from './aiChatToolConfirmations.reducer.utils';
import {
  appendPendingQuestion,
  mergePendingQuestionsForMessage,
  updateQuestionStatus,
} from './aiChatUserQuestions.reducer.utils';

const initialState = {
  session: null,
  sessionId: null,
  loanId: null,
  configuration: null,
  lastMessageDate: null,
  messages: [],
  isLoading: false,
  isSending: false,
  isAwaitingAssistantResponse: false,
  awaitingAssistantStartedAt: null,
  awaitingAssistantLastActivityAt: null,
  awaitingAssistantMessageId: null,
  isAssistantSlow: false,
  error: null,
  isPanelOpen: false,
  unreadCount: 0,
  executionPlan: null,
  executionPlans: {},
  actionPlanExpansions: initialActionPlanExpansionsState,
  guidelineFullView: null,
  guidelineCache: {},
  isLoadingGuideline: false,
  pendingToolExecution: null,
  toolExecutionHistory: [],
  citations: {},
  citationsBlobLoaded: {},
  messageContentBlocks: {},
  eligibleConfigurations: [],
  isLoadingEligibleConfigurations: false,
  eligibleConfigurationsError: null,
  isSwitchingConfiguration: false,
  switchConfigurationError: null,
  pendingToolConfirmations: {},
  pendingUserQuestions: {},
  isConnected: false,
};

const aiChatReducer = (state = initialState, action) => {
  const { type, payload, meta } = action;

  switch (type) {
    case AI_CHAT_ACTION_TYPE.GET_SESSION:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AI_CHAT_ACTION_TYPE.GET_SESSION_SUCCESS:
      return {
        ...state,
        session: payload,
        isLoading: false,
      };

    case AI_CHAT_ACTION_TYPE.GET_SESSION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: payload,
      };

    case AI_CHAT_ACTION_TYPE.GET_MESSAGES:
      // DEV-4392: persist the route-supplied loanId so the SignalR reconnect
      // handler can re-fetch history (selectAiChatLoanId) without depending on
      // the session DTO's loanId serialization.
      return {
        ...state,
        loanId: payload?.loanId ?? state.loanId,
        isLoading: true,
        error: null,
      };

    case AI_CHAT_ACTION_TYPE.GET_MESSAGES_SUCCESS:
      return {
        ...state,
        messages: payload,
        isLoading: false,
      };

    case AI_CHAT_ACTION_TYPE.GET_MESSAGES_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: payload,
      };

    case AI_CHAT_ACTION_TYPE.SEND_MESSAGE: {
      const now = new Date().toISOString();

      return {
        ...state,
        isSending: true,
        isAwaitingAssistantResponse: true,
        awaitingAssistantStartedAt: now,
        awaitingAssistantLastActivityAt: now,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
        error: null,
      };
    }

    case AI_CHAT_ACTION_TYPE.SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        messages: reconcileSentMessage(state.messages, payload, meta?.clientTempId),
        isSending: false,
        awaitingAssistantMessageId: payload?.id ?? state.awaitingAssistantMessageId,
      };

    case AI_CHAT_ACTION_TYPE.SEND_MESSAGE_FAILURE:
      return {
        ...state,
        messages: markOptimisticMessageFailed(state.messages, meta?.clientTempId),
        isSending: false,
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
        error: payload,
      };

    case AI_CHAT_ACTION_TYPE.RETRY_MESSAGE: {
      const now = new Date().toISOString();

      return {
        ...state,
        messages: removeFailedAssistantPlaceholderForMessage(state.messages, payload?.messageId),
        isAwaitingAssistantResponse: true,
        awaitingAssistantStartedAt: now,
        awaitingAssistantLastActivityAt: now,
        awaitingAssistantMessageId: payload?.messageId ?? null,
        isAssistantSlow: false,
        error: null,
      };
    }

    case AI_CHAT_ACTION_TYPE.RETRY_MESSAGE_SUCCESS:
      return state;

    case AI_CHAT_ACTION_TYPE.RETRY_MESSAGE_FAILURE:
      return {
        ...state,
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
        messages: applyMessageFailedToMessages(state.messages, {
          messageId: meta?.messageId ?? null,
          error: payload,
          canRetry: true,
        }),
      };

    case AI_CHAT_ACTION_TYPE.ADD_MESSAGE: {
      const isAssistantWhilePanelClosed =
        payload?.role === AiChatMessageRole.ASSISTANT && !state.isPanelOpen;

      return {
        ...state,
        messages: [...state.messages, payload],
        unreadCount: isAssistantWhilePanelClosed ? state.unreadCount + 1 : state.unreadCount,
      };
    }

    case AI_CHAT_ACTION_TYPE.UPDATE_MESSAGE: {
      const messageIndex = state.messages.findIndex(msg => msg.id === payload.id);

      if (messageIndex === -1) {
        return state;
      }

      const updatedMessages = [...state.messages];
      updatedMessages[messageIndex] = { ...updatedMessages[messageIndex], ...payload };

      return {
        ...state,
        messages: updatedMessages,
      };
    }

    case AI_CHAT_ACTION_TYPE.SET_MESSAGE_FEEDBACK:

    case AI_CHAT_ACTION_TYPE.SET_MESSAGE_FEEDBACK_FAILURE: {
      const updatedMessages = applyMessageFeedbackOutcome(state.messages, payload);

      if (updatedMessages === state.messages) {
        return state;
      }

      return {
        ...state,
        messages: updatedMessages,
      };
    }

    case AI_CHAT_ACTION_TYPE.SET_IS_LOADING:
      return {
        ...state,
        isLoading: payload,
      };

    case AI_CHAT_ACTION_TYPE.SET_IS_SENDING:
      return {
        ...state,
        isSending: payload,
      };

    case AI_CHAT_ACTION_TYPE.SET_ERROR:
      return {
        ...state,
        error: payload,
      };

    case AI_CHAT_ACTION_TYPE.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AI_CHAT_ACTION_TYPE.SET_IS_PANEL_OPEN:
      return {
        ...state,
        isPanelOpen: payload,
        unreadCount: payload ? 0 : state.unreadCount,
      };

    case AI_CHAT_ACTION_TYPE.SET_IS_CONNECTED:
      return {
        ...state,
        isConnected: payload,
      };

    case AI_CHAT_ACTION_TYPE.SET_EXECUTION_PLAN:
      return {
        ...state,
        executionPlan: payload,
      };

    case AI_CHAT_ACTION_TYPE.UPDATE_EXECUTION_PLAN_STEP: {
      if (!state.executionPlan) {
        return state;
      }

      const stepIndex = state.executionPlan.steps.findIndex(step => step.id === payload.id);

      if (stepIndex === -1) {
        return state;
      }

      const updatedSteps = [...state.executionPlan.steps];
      updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], ...payload };

      return {
        ...state,
        executionPlan: {
          ...state.executionPlan,
          steps: updatedSteps,
        },
      };
    }

    case AI_CHAT_ACTION_TYPE.CLEAR_EXECUTION_PLAN:
      return {
        ...state,
        executionPlan: null,
      };

    case AI_CHAT_ACTION_TYPE.SET_GUIDELINE_FULL_VIEW:
      return {
        ...state,
        guidelineFullView: payload,
        isLoadingGuideline: !!payload && !state.guidelineCache[payload?.id],
      };

    case AI_CHAT_ACTION_TYPE.FETCH_GUIDELINE_CONTENT_FAILURE:
      return {
        ...state,
        isLoadingGuideline: false,
      };

    case AI_CHAT_ACTION_TYPE.CACHE_GUIDELINE_CONTENT:
      return {
        ...state,
        guidelineCache: {
          ...state.guidelineCache,
          [payload.id]: payload.content,
        },
        isLoadingGuideline: false,
      };

    case AI_CHAT_ACTION_TYPE.CLEAR_GUIDELINE_CACHE:
      return {
        ...state,
        guidelineCache: {},
      };

    case AI_CHAT_ACTION_TYPE.SET_PENDING_TOOL_EXECUTION: {
      const isPendingWhilePanelClosed = !!payload && !state.isPanelOpen;

      return {
        ...state,
        pendingToolExecution: payload,
        // DEV-2913 follow-up: lazily create the assistant bubble for the
        // suspended turn's messageId. The BE suspends on a frontend-routed
        // tool (open_loan_section) BEFORE any synthesis text streams, so no
        // response_token or message_content_block_added ever fires for this
        // turn — which means the messages[] array never gets an entry for
        // the assistantMessageId. ToolConfirmationCard is rendered per-bubble
        // and filters by `pendingToolExecution.messageId === bubble.id`, so
        // without a bubble the confirmation card never appears (the user
        // sees only the session-level "Awaiting approval" bar with no card
        // to click). Same pattern as the existing
        // MESSAGE_CONTENT_BLOCK_ADDED / _UPDATED handlers below.
        messages: ensureStreamingAssistantPlaceholder(state.messages, payload),
        unreadCount: isPendingWhilePanelClosed ? state.unreadCount + 1 : state.unreadCount,
      };
    }

    case AI_CHAT_ACTION_TYPE.CLEAR_PENDING_TOOL_EXECUTION:
      return {
        ...state,
        pendingToolExecution: null,
      };

    case AI_CHAT_ACTION_TYPE.ADD_TOOL_EXECUTION_RESULT:
      return {
        ...state,
        toolExecutionHistory: [...state.toolExecutionHistory, payload],
      };

    case AI_CHAT_ACTION_TYPE.CLEAR_TOOL_EXECUTION_HISTORY:
      return {
        ...state,
        toolExecutionHistory: [],
      };

    case AI_CHAT_ACTION_TYPE.ENSURE_SESSION:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AI_CHAT_ACTION_TYPE.ENSURE_SESSION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        session: payload,
        sessionId: payload?.sessionId ?? null,
        configuration: payload?.configuration ?? null,
        lastMessageDate: payload?.lastMessageDate ?? null,
      };

    case AI_CHAT_ACTION_TYPE.ENSURE_SESSION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: payload,
      };

    case AI_CHAT_ACTION_TYPE.RESPONSE_TOKEN:
      return {
        ...state,
        messages: applyResponseTokenToMessages(state.messages, payload),
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
      };

    case AI_CHAT_ACTION_TYPE.RESPONSE_COMPLETE:
      return {
        ...state,
        messages: applyResponseCompleteToMessages(state.messages, payload),
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
      };

    case AI_CHAT_ACTION_TYPE.PLAN_PLANNING_STARTED:
      return {
        ...applyAssistantActivity(state),
        // DEV-2913 follow-up: lazily create the assistant bubble keyed by the
        // turn's assistantMessageId. ActionPlan renders per-bubble inside
        // BotMessage; without a bubble entry the plan card never mounts even
        // though `executionPlans` state is populated. The bubble is normally
        // created on the first response_token, but planning/confirmation
        // events fire before any token streams (and may suspend before any
        // ever fires). Mirrors the SET_PENDING_TOOL_EXECUTION fix below.
        messages: ensureStreamingAssistantPlaceholder(state.messages, payload),
        executionPlans: applyPlanPlanningStarted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_CREATED:
      return {
        ...applyAssistantActivity(state),
        // DEV-2913 follow-up: see PLAN_PLANNING_STARTED rationale above.
        messages: ensureStreamingAssistantPlaceholder(state.messages, payload),
        executionPlans: applyPlanCreated(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_UPDATED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanUpdated(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.STEP_STARTED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepStarted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.STEP_COMPLETED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepCompleted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.STEP_FAILED:
      return state;

    case AI_CHAT_ACTION_TYPE.PLAN_REVISED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanRevised(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_STARTED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepStarted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_TOOL_DISPATCHED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepToolDispatched(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_TOOL_RETURNED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepToolReturned(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_COMPLETED:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepCompleted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_FAILED:
      return {
        ...state,
        executionPlans: applyPlanStepFailed(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_SKIPPED:
      return {
        ...state,
        executionPlans: applyPlanStepSkipped(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_AWAITING_CONFIRMATION:
      return {
        ...applyAssistantActivity(state),
        executionPlans: applyPlanStepAwaitingConfirmation(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_STEP_CANCELLED:
      return {
        ...state,
        executionPlans: applyPlanStepCancelled(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_COMPLETED:
      return {
        ...state,
        executionPlans: applyPlanCompleted(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.PLAN_CANCELLED:
      return {
        ...state,
        executionPlans: applyPlanCancelled(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.CLEAR_PLAN_REVISION_FLAGS:
      return {
        ...state,
        executionPlans: applyClearPlanRevisionFlags(state.executionPlans, payload),
      };

    case AI_CHAT_ACTION_TYPE.SET_ACTION_PLAN_EXPANSION:
      return {
        ...state,
        actionPlanExpansions: applySetActionPlanExpansion(state.actionPlanExpansions, payload),
      };

    case AI_CHAT_ACTION_TYPE.EXECUTION_CANCELLED:
      return {
        ...state,
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
      };

    case AI_CHAT_ACTION_TYPE.SESSION_STOPPED:
      return {
        ...state,
        messages: applySessionStoppedToMessages(state.messages, payload),
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
      };

    case AI_CHAT_ACTION_TYPE.MESSAGE_FAILED:
      return {
        ...state,
        messages: applyMessageFailedToMessages(state.messages, payload),
        isAwaitingAssistantResponse: false,
        awaitingAssistantStartedAt: null,
        awaitingAssistantLastActivityAt: null,
        awaitingAssistantMessageId: null,
        isAssistantSlow: false,
      };

    case AI_CHAT_ACTION_TYPE.SET_ASSISTANT_SLOW:
      return {
        ...state,
        isAssistantSlow: !!payload,
      };

    case AI_CHAT_ACTION_TYPE.TOOL_STARTED:
      return {
        ...applyAssistantActivity(state),
        messages: applyToolStartedToMessages(state.messages, payload),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_COMPLETED:
      return {
        ...applyAssistantActivity(state),
        messages: applyToolCompletedToMessages(state.messages, payload),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_CONFIRMATION_REQUESTED:
      return {
        ...state,
        pendingToolConfirmations: appendPendingConfirmation(
          state.pendingToolConfirmations,
          payload,
        ),
      };

    case AI_CHAT_ACTION_TYPE.APPROVE_TOOL_CALL:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: { status: ToolConfirmationStatus.APPROVING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.REJECT_TOOL_CALL:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: { status: ToolConfirmationStatus.REJECTING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.APPROVE_TOOL_CALL_FAILURE:
    case AI_CHAT_ACTION_TYPE.REJECT_TOOL_CALL_FAILURE:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: { status: ToolConfirmationStatus.PENDING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_EXECUTED:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: {
            status: ToolConfirmationStatus.EXECUTED,
            result: payload?.result ?? null,
          },
        }),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_FAILED:
      return {
        ...state,
        messages: applyToolFailedToMessages(state.messages, payload),
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: {
            status: ToolConfirmationStatus.FAILED,
            error: payload?.error ?? null,
          },
        }),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_CANCELLED:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: {
            status: ToolConfirmationStatus.CANCELLED,
            reason: payload?.reason ?? null,
          },
        }),
      };

    case AI_CHAT_ACTION_TYPE.TOOL_CONFIRMATION_TIMEOUT:
      return {
        ...state,
        pendingToolConfirmations: updateConfirmationStatus(state.pendingToolConfirmations, {
          messageId: payload?.messageId,
          toolCallId: payload?.toolCallId,
          changes: { status: ToolConfirmationStatus.TIMED_OUT },
        }),
      };

    case AI_CHAT_ACTION_TYPE.LOAD_PENDING_TOOL_CONFIRMATIONS_SUCCESS:
      return {
        ...state,
        pendingToolConfirmations: mergePendingConfirmationsForMessage(
          state.pendingToolConfirmations,
          {
            messageId: payload?.messageId,
            confirmations: payload?.confirmations ?? [],
          },
        ),
      };

    case AI_CHAT_ACTION_TYPE.USER_QUESTION_REQUESTED:
      return {
        ...state,
        pendingUserQuestions: appendPendingQuestion(state.pendingUserQuestions, payload),
      };

    case AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION:
      return {
        ...state,
        pendingUserQuestions: updateQuestionStatus(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questionId: payload?.questionId,
          changes: { status: UserQuestionStatus.SUBMITTING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION_SUCCESS:
      return {
        ...state,
        pendingUserQuestions: updateQuestionStatus(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questionId: payload?.questionId,
          changes: { status: UserQuestionStatus.ANSWERED },
        }),
      };

    case AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION:
      return {
        ...state,
        pendingUserQuestions: updateQuestionStatus(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questionId: payload?.questionId,
          changes: { status: UserQuestionStatus.DISMISSING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION_SUCCESS:
      return {
        ...state,
        pendingUserQuestions: updateQuestionStatus(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questionId: payload?.questionId,
          changes: { status: UserQuestionStatus.DISMISSED },
        }),
      };

    case AI_CHAT_ACTION_TYPE.RESPOND_TO_USER_QUESTION_FAILURE:
    case AI_CHAT_ACTION_TYPE.DISMISS_USER_QUESTION_FAILURE:
      return {
        ...state,
        pendingUserQuestions: updateQuestionStatus(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questionId: payload?.questionId,
          changes: { status: UserQuestionStatus.PENDING },
        }),
      };

    case AI_CHAT_ACTION_TYPE.LOAD_PENDING_QUESTIONS_SUCCESS:
      return {
        ...state,
        pendingUserQuestions: mergePendingQuestionsForMessage(state.pendingUserQuestions, {
          messageId: payload?.messageId,
          questions: payload?.questions ?? [],
        }),
      };

    case AI_CHAT_ACTION_TYPE.CITATIONS_EXTRACTED:
      return {
        ...state,
        messages: applyCitationsFlagToMessages(state.messages, payload),
        citations: applyCitationsForMessage(state.citations, payload),
      };

    case AI_CHAT_ACTION_TYPE.CITATIONS_BLOB_LOADED:
      return {
        ...state,
        messages: applyCitationsFlagToMessages(state.messages, payload),
        citations: applyCitationsForMessage(state.citations, payload),
        citationsBlobLoaded: {
          ...state.citationsBlobLoaded,
          [payload?.messageId]: true,
        },
      };

    case AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCK_ADDED:
      return {
        ...applyAssistantActivity(state),
        messages: ensureStreamingAssistantPlaceholder(state.messages, payload),
        messageContentBlocks: applyBlockAdded(state.messageContentBlocks, payload),
      };

    case AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCK_UPDATED:
      return {
        ...applyAssistantActivity(state),
        messages: ensureStreamingAssistantPlaceholder(state.messages, payload),
        messageContentBlocks: applyBlockUpdated(state.messageContentBlocks, payload),
      };

    case AI_CHAT_ACTION_TYPE.MESSAGE_CONTENT_BLOCKS_LOADED:
      return {
        ...state,
        messageContentBlocks: applyBlocksLoaded(state.messageContentBlocks, payload),
      };

    case AI_CHAT_ACTION_TYPE.MESSAGE_RECEIVED:
      return {
        ...applyAssistantActivity(state),
        unreadCount: state.isPanelOpen ? state.unreadCount : state.unreadCount + 1,
      };

    case AI_CHAT_ACTION_TYPE.LOAD_UNREAD_COUNT_SUCCESS:
      return {
        ...state,
        unreadCount: payload?.unreadCount ?? 0,
      };

    case AI_CHAT_ACTION_TYPE.MARK_CHAT_READ_SUCCESS:
      return {
        ...state,
        unreadCount: 0,
      };

    case AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS:
      return {
        ...state,
        isLoadingEligibleConfigurations: true,
        eligibleConfigurationsError: null,
      };

    case AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS_SUCCESS:
      return {
        ...state,
        isLoadingEligibleConfigurations: false,
        eligibleConfigurations: payload?.configurations ?? [],
      };

    case AI_CHAT_ACTION_TYPE.LOAD_ELIGIBLE_CONFIGURATIONS_FAILURE:
      return {
        ...state,
        isLoadingEligibleConfigurations: false,
        eligibleConfigurationsError: payload,
      };

    case AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION:
      return {
        ...state,
        isSwitchingConfiguration: true,
        switchConfigurationError: null,
      };

    case AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION_SUCCESS:
      return {
        ...state,
        isSwitchingConfiguration: false,
        session: payload,
        sessionId: payload?.sessionId ?? null,
        configuration: payload?.configuration ?? null,
        lastMessageDate: payload?.lastMessageDate ?? null,
        messages: [],
        executionPlans: {},
        citations: {},
        messageContentBlocks: {},
        pendingToolExecution: null,
        toolExecutionHistory: [],
        pendingToolConfirmations: {},
        pendingUserQuestions: {},
      };

    case AI_CHAT_ACTION_TYPE.SWITCH_CONFIGURATION_FAILURE:
      return {
        ...state,
        isSwitchingConfiguration: false,
        switchConfigurationError: payload,
      };

    case AI_CHAT_ACTION_TYPE.RESET_STATE:
    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default aiChatReducer;
